/**
 * Created by Rodrigo Quesada on 09/04/17.
 */
import {Model, ModelMixin} from '../common/model.class';
import {mapDictionary} from '../../../common/typescript/objects.util';

//region Other Classes
export type SectionMap<S extends Section> = { [key: string]: S };

export class Section {

    readonly sectionName?: string;
    readonly completed: boolean;
    readonly ignore: boolean;

    constructor(jsonData: any = {}) {
        Object.assign(this, jsonData);
    }
}

export class RepeatableSection<S extends Section> extends Section {

    readonly entries: SectionMap<S>;

    constructor(jsonData: any = {}, newSection: new (any) => S) {
        jsonData.entries = mapDictionary(jsonData.entries, it => new newSection(it));
        super(jsonData);
    }
}

export class SimpleRepeatableSection extends RepeatableSection<Section> {

    constructor(jsonData: any = {}) {
        super(jsonData, Section);
    }
}

export class PersonalSection extends Section {

    readonly userInfo: Section;
    readonly practiceSpecialties: Section;
    readonly address: Section;
    readonly contact: Section;
    readonly identification: Section;

    constructor(jsonData: any = {}) {
        jsonData.userInfo = new Section(jsonData.userInfo);
        jsonData.practiceSpecialties = new Section(jsonData.practiceSpecialties);
        jsonData.address = new Section(jsonData.address);
        jsonData.contact = new Section(jsonData.contact);
        jsonData.identification = new Section(jsonData.identification);
        super(jsonData);
    }
}

export class EducationSection extends Section {

    readonly ecfmg: Section;
    readonly premedical: Section;
    readonly medical: Section;
    readonly other: SimpleRepeatableSection;
    readonly internship: SimpleRepeatableSection;
    readonly residency: SimpleRepeatableSection;
    readonly fellowshipOrPreceptorship: SimpleRepeatableSection;

    constructor(jsonData: any = {}) {
        jsonData.ecfmg = new Section(jsonData.ecfmg);
        jsonData.premedical = new Section(jsonData.premedical);
        jsonData.medical = new Section(jsonData.medical);
        jsonData.other = new SimpleRepeatableSection(jsonData.other);
        jsonData.internship = new SimpleRepeatableSection(jsonData.internship);
        jsonData.residency = new SimpleRepeatableSection(jsonData.residency);
        jsonData.fellowshipOrPreceptorship = new SimpleRepeatableSection(jsonData.fellowshipOrPreceptorship);
        super(jsonData);
    }
}

export class WorkSection extends Section {

    readonly work: SimpleRepeatableSection;
    readonly activityLogs: Section;
    readonly military: Section;
    readonly observations: Section;

    constructor(jsonData: any = {}) {
        jsonData.work = new SimpleRepeatableSection(jsonData.work);
        jsonData.activityLogs = new Section(jsonData.activityLogs);
        jsonData.military = new Section(jsonData.military);
        jsonData.observations = new Section(jsonData.observations);
        super(jsonData);
    }
}

export class LicensesAndCertificationsSection extends Section {

    readonly board: BoardsSection;
    readonly professionalLicenses: ProfessionalLicensesSection;
    readonly inactiveLicenses: Section;
    readonly dea: DeaSection;
    readonly lifeCertifications: SimpleRepeatableSection;
    readonly licensingExaminations: SimpleRepeatableSection;
    readonly cme: CMESection;

    constructor(jsonData: any = {}) {
        jsonData.board = new BoardsSection(jsonData.board);
        jsonData.professionalLicenses = new ProfessionalLicensesSection(jsonData.professionalLicenses);
        jsonData.inactiveLicenses = new Section(jsonData.inactiveLicenses);
        jsonData.dea = new DeaSection(jsonData.dea);
        jsonData.lifeCertifications = new SimpleRepeatableSection(jsonData.lifeCertifications);
        jsonData.licensingExaminations = new SimpleRepeatableSection(jsonData.licensingExaminations);
        jsonData.cme = new CMESection(jsonData.cme);
        super(jsonData);
    }
}

export class BoardsSection extends Section {

    readonly abmsBoard: RepeatableBoardSection;
    readonly abpsBoard: SimpleRepeatableSection;
    readonly aoaBoard: RepeatableBoardSection;
    readonly boardAffiliationInformation: Section;

    constructor(jsonData: any = {}) {
        jsonData.abmsBoard = new RepeatableBoardSection(jsonData.abmsBoard);
        jsonData.abpsBoard = new SimpleRepeatableSection(jsonData.abpsBoard);
        jsonData.aoaBoard = new RepeatableBoardSection(jsonData.aoaBoard);
        jsonData.boardAffiliationInformation = new Section(jsonData.boardAffiliationInformation);
        super(jsonData);
    }
}

export class RepeatableBoardSection extends RepeatableSection<BoardWithSubspecialtiesSection> {

    constructor(jsonData: any = {}) {
        super(jsonData, BoardWithSubspecialtiesSection);
    }
}

export class BoardWithSubspecialtiesSection extends Section {

    readonly main: Section;
    readonly subspecialties: SimpleRepeatableSection;

    constructor(jsonData: any = {}) {
        jsonData.main = new Section(jsonData.main);
        jsonData.subspecialties = new SimpleRepeatableSection(jsonData.subspecialties);
        super(jsonData);
    }
}

export class ProfessionalLicensesSection extends RepeatableSection<ProfessionalLicenseSection> {

    constructor(jsonData: any = {}) {
        super(jsonData, ProfessionalLicenseSection);
    }
}

export class ProfessionalLicenseSection extends Section {

    readonly license: Section;
    readonly permit: Section;

    constructor(jsonData: any = {}) {
        jsonData.license = new Section(jsonData.license);
        jsonData.permit = new Section(jsonData.permit);
        super(jsonData);
    }
}

export class DeaSection extends Section {

    readonly data: SimpleRepeatableSection;
    readonly info: Section;

    constructor(jsonData: any = {}) {
        jsonData.data = new SimpleRepeatableSection(jsonData.data);
        jsonData.info = new Section(jsonData.info);
        super(jsonData);
    }
}

export class CMESection extends Section {

    readonly data: SimpleRepeatableSection;
    readonly info: Section;

    constructor(jsonData: any = {}) {
        jsonData.data = new SimpleRepeatableSection(jsonData.data);
        jsonData.info = new Section(jsonData.info);
        super(jsonData);
    }
}

export class ProfessionalLiabilitySection extends Section {

    readonly malpractice: Section;
    readonly licensesAndAffiliations: Section;
    readonly disciplinaryActions: Section;
    readonly healthStatus: Section;

    constructor(jsonData: any = {}) {
        jsonData.malpractice = new Section(jsonData.malpractice);
        jsonData.licensesAndAffiliations = new Section(jsonData.licensesAndAffiliations);
        jsonData.disciplinaryActions = new Section(jsonData.disciplinaryActions);
        jsonData.healthStatus = new Section(jsonData.healthStatus);
        super(jsonData);
    }
}
//endregion

export class ProfileCompletion extends ModelMixin() implements Model<ProfileCompletion> {

    TYPE = ProfileCompletion;

    readonly completed: boolean;
    readonly personal: PersonalSection;
    readonly education: EducationSection;
    readonly work: WorkSection;
    readonly licensesAndCertifications: LicensesAndCertificationsSection;
    readonly professionalLiability: ProfessionalLiabilitySection;
    readonly references: SimpleRepeatableSection;
    readonly releaseAuthorization: Section;

    constructor(jsonData: any = {}) {
        super();
        jsonData.personal = new PersonalSection(jsonData.personal);
        jsonData.education = new EducationSection(jsonData.education);
        jsonData.work = new WorkSection(jsonData.work);
        jsonData.licensesAndCertifications = new LicensesAndCertificationsSection(jsonData.licensesAndCertifications);
        jsonData.professionalLiability = new ProfessionalLiabilitySection(jsonData.professionalLiability);
        jsonData.references = new SimpleRepeatableSection(jsonData.references);
        jsonData.releaseAuthorization = new Section(jsonData.releaseAuthorization);
        Object.assign(this, jsonData);
    }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/tslint-loader!./src/app/domain/model/completion/profile-completion.model.ts